import { useCallback, useMemo, useRef, useState } from "react";
import { Button, Typography, message } from "antd";
import PageTitle from "../../components/title/pageTitle";
import Navigation from "../../components/navigation/navigation";
import Footer from "../../components/footer/footer";
import BackHomeIcon, { PANEL_PATH } from "../../components/icons/BackHome/backhome";
import QuoteForm from "../../components/quote/QuoteForm";
import QuotePreviewPanel from "../../components/quote/QuotePreviewPanel";
import { createDefaultFormState } from "../../utils/quoteDefaults";
import { buildQuoteDisplay, validateQuoteForm } from "../../utils/quoteFormat";
import { useTranslation } from "../../i18n";
import "./quote.css";

const { Paragraph } = Typography;

function QuoteBuilder() {
    const { t } = useTranslation();
    const previewRef = useRef(null);
    const [formState, setFormState] = useState(() => createDefaultFormState());
    const [showPreview, setShowPreview] = useState(false);
    const [validationError, setValidationError] = useState("");

    const quoteDisplay = useMemo(() => buildQuoteDisplay(formState), [formState]);

    const handlePreview = useCallback(() => {
        const error = validateQuoteForm(formState, t);
        setValidationError(error);
        if (error) {
            message.error(error);
            return;
        }

        setShowPreview(true);
        window.setTimeout(() => {
            previewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
    }, [formState, t]);

    const handleReset = useCallback(() => {
        setFormState(createDefaultFormState());
        setShowPreview(false);
        setValidationError("");
        message.success(t("quote.page.resetDone"));
    }, [t]);

    return (
        <>
            <PageTitle>
                <Navigation />
            </PageTitle>
            <BackHomeIcon to={PANEL_PATH} />

            <div className="quote-page">
                <div className="custom-h1">
                    <span>{t("quote.page.title")}</span>
                </div>
                <Paragraph className="quote-page__subtitle">{t("quote.page.subtitle")}</Paragraph>

                <div className="quote-page__layout">
                    <QuoteForm
                        formState={formState}
                        onChange={setFormState}
                        validationError={validationError}
                    />

                    <div className="quote-page__actions">
                        <Button type="primary" onClick={handlePreview}>
                            {t("quote.page.preview")}
                        </Button>
                        <Button onClick={handleReset}>{t("quote.page.reset")}</Button>
                    </div>

                    <div ref={previewRef}>
                        <QuotePreviewPanel quoteDisplay={quoteDisplay} visible={showPreview} />
                    </div>
                </div>
            </div>

            <Footer />
        </>
    );
}

export default QuoteBuilder;
